import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useFunnel } from "../context/FunnelContext";
import { StepHeader } from "../components/StepHeader";
import { ProgressBar } from "../components/ProgressBar";
import { EmailReflectionCapture } from "../components/EmailReflectionCapture";

const PILLARS = {
  anxiety: {
    title: "Calm & Nervous System Safety",
    text: "Together, we'll gently work with your nervous system — learning to recognise anxiety as it arises and creating real, embodied moments of calm you can return to, again and again.",
  },
  burnout: {
    title: "Rest, Boundaries & Reconnection",
    text: "We'll slow down together. Sessions make space for genuine rest, honest boundaries, and reconnecting with the parts of you that burnout asked you to set aside.",
  },
  disconnection: {
    title: "Inner Guidance & Meaning",
    text: "We'll create quiet space to listen inward — reconnecting you with your intuition, your sense of purpose, and the spiritual thread that feels most true to you.",
  },
  growth: {
    title: "Deeper Spiritual Growth",
    text: "Wherever you already are, there's more to uncover. We'll go deeper into your inner world at a pace that honours your readiness.",
  },
};

export default function OfferPage() {
  const { getSegment } = useFunnel();
  const navigate = useNavigate();
  const segment = getSegment();
  const pillar = PILLARS[segment] || PILLARS.growth;

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      data-testid="offer-page"
      className="min-h-screen px-6 py-28 bg-[#FBF9F5]"
    >
      <ProgressBar step={4} total={6} />
      <div className="max-w-2xl mx-auto">
        <StepHeader step={4} total={6} label="Your Offer" />
        <h1 className="font-serif text-3xl sm:text-4xl text-[#1E2D24] mb-8">
          What is soul-centred spiritual coaching?
        </h1>
        <div className="space-y-5 text-[#5F6A62] text-base sm:text-lg leading-relaxed">
          <p>
            It's a safe, guided space to explore your inner world — your emotions, your thoughts, and your
            spiritual self — without judgment, and without needing to have it all figured out.
          </p>
          <p>
            This isn't about fixing you. There's nothing broken here. It's about gently supporting you in
            reconnecting with your own inner wisdom — the guidance that's been quietly waiting underneath
            the noise.
          </p>
          <p>
            Every session moves at your pace, shaped around your sensitivity and your unique journey.
            Nothing is rushed. Nothing is forced.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mt-10 p-6 sm:p-8 rounded-2xl bg-[#E2EAE4] border border-[#4A6B56]/15"
          data-testid="personalized-pillar"
        >
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#4A6B56]/80 mb-2">
            Based on what you shared
          </p>
          <h2 className="font-serif text-xl sm:text-2xl text-[#1E2D24] mb-3">{pillar.title}</h2>
          <p className="text-[#3A4A3E] leading-relaxed">{pillar.text}</p>
        </motion.div>

        <button
          data-testid="continue-to-booking-button"
          onClick={() => navigate("/book")}
          className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#4A6B56] text-[#FBF9F5] font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
        >
          See how we can work together <ArrowRight className="w-4 h-4" />
        </button>

        <EmailReflectionCapture segment={segment} />
      </div>
    </motion.main>
  );
}
